import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { videosApi } from '../services/api';
import { ArrowRight, Shield, Users, FileText, PlayCircle, Search, X } from 'lucide-react';

function LoginPage() {
  const { isAuthenticated, loading, login } = useAuth();
  const [videos, setVideos] = useState([]);
  const [videosLoading, setVideosLoading] = useState(true);
  const [videoError, setVideoError] = useState('');
  const [videoSearch, setVideoSearch] = useState('');
  const [showVideoBrowser, setShowVideoBrowser] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    let mounted = true;

    async function fetchPublicVideos() {
      try {
        const data = await videosApi.getPublished();
        if (mounted) setVideos(Array.isArray(data) ? data : []);
      } catch (err) {
        if (mounted) setVideoError(err.message || 'Unable to load videos');
      } finally {
        if (mounted) setVideosLoading(false);
      }
    }

    fetchPublicVideos();
    return () => { mounted = false; };
  }, []);

  const filteredVideos = useMemo(() => {
    const query = videoSearch.trim().toLowerCase();
    if (!query) return videos;
    return videos.filter(video => {
      const title = video.title?.toLowerCase() || '';
      const tags = (video.tags || []).map(tag => tag.name?.toLowerCase()).join(' ');
      return title.includes(query) || tags.includes(query);
    });
  }, [videoSearch, videos]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="rounded-lg bg-black px-2 py-1.5 shadow-sm animate-pulse">
            <img src="/logo.png" alt="i2e Consulting" className="w-[4.5rem] h-auto object-contain" />
          </div>
          <div className="text-gray-500">Loading...</div>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const features = [
    { icon: FileText, title: 'Policy Hub', desc: 'Access all company policies in one place' },
    { icon: Users, title: 'Team Resources', desc: 'Training videos and onboarding materials' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Left Panel - Hero */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-[#00AAFF] to-[#0088cc]">
        {/* Pattern Background */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center p-12 xl:p-20">
          <div className="flex items-center gap-3 mb-8">
            <div className="rounded-lg bg-black px-2.5 py-2 shadow-lg">
              <img src="/logo.png" alt="i2e Consulting" className="w-24 h-auto object-contain" />
            </div>
          </div>

          <h1 className="text-4xl xl:text-5xl font-bold text-white mb-6 leading-tight">
            Resource Center
          </h1>

          <p className="text-lg text-white/90 mb-12 max-w-md">
            Access company policies, training videos, and important resources all in one centralized platform.
          </p>

          {/* Feature Cards */}
          <div className="space-y-4">
            {features.map(({ icon: Icon, title, desc }, i) => (
              <div
                key={title}
                className="flex items-start gap-4 p-4 rounded-xl bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-colors animate-slide-in"
                style={{ animationDelay: `${i * 100}ms` }}
              >
                <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">{title}</h3>
                  <p className="text-sm text-white/80">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center mb-12">
            <div className="rounded-lg bg-black px-2.5 py-2 shadow-lg">
              <img src="/logo.png" alt="i2e Consulting" className="w-24 max-w-[35vw] h-auto object-contain" />
            </div>
          </div>

          {/* Login Card */}
          <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100 animate-fade-in">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome back</h2>
              <p className="text-gray-500">Sign in to access your dashboard</p>
            </div>

            {/* Microsoft Sign In Button */}
            <button
              onClick={login}
              className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-white hover:bg-gray-50 text-gray-800 font-medium rounded-xl transition-all duration-200 shadow-sm border-2 border-[#00AAFF] hover:shadow-lg hover:-translate-y-0.5 group"
            >
              <svg viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg" width="21" height="21">
                <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
              </svg>
              <span>Sign in with Microsoft</span>
              <ArrowRight className="w-4 h-4 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
            </button>

            {/* Divider */}
            <div className="flex items-center gap-4 my-8">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-400 uppercase tracking-wider">Secure login</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            {/* Info */}
            <div className="flex items-start gap-3 p-4 rounded-xl bg-[#00AAFF]/5 border border-[#00AAFF]/20">
              <Shield className="w-5 h-5 text-[#00AAFF] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-gray-600">
                  Use your organization Microsoft account to sign in securely.
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <p className="text-center text-sm text-gray-400 mt-8">
            By signing in, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>

      {showVideoBrowser && !selectedVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-950/70 animate-fade-in" onClick={() => setShowVideoBrowser(false)}>
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3 p-5 border-b border-gray-100">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Browse training videos</h3>
                <p className="text-sm text-gray-500">Available without signing in</p>
              </div>
              <button
                type="button"
                onClick={() => setShowVideoBrowser(false)}
                className="w-10 h-10 rounded-xl hover:bg-gray-100 flex items-center justify-center transition-colors"
                aria-label="Close training videos"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-5">
              <div className="flex items-center gap-2 h-11 px-3 mb-4 rounded-xl bg-gray-50 border border-gray-200">
                <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <input
                  type="text"
                  value={videoSearch}
                  onChange={e => setVideoSearch(e.target.value)}
                  placeholder="Search videos"
                  className="w-full bg-transparent border-none outline-none text-sm text-gray-700 placeholder-gray-400"
                />
              </div>

              {videosLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map(i => <div key={i} className="h-14 rounded-xl bg-gray-100 animate-pulse" />)}
                </div>
              ) : videoError ? (
                <div className="rounded-xl bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-600">
                  {videoError}
                </div>
              ) : filteredVideos.length === 0 ? (
                <div className="rounded-xl bg-gray-50 border border-gray-100 px-4 py-8 text-center text-sm text-gray-500">
                  {videoSearch ? 'No videos match your search.' : 'No training videos are published yet.'}
                </div>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                  {filteredVideos.map(video => (
                    <button
                      key={video.id}
                      type="button"
                      onClick={() => setSelectedVideo(video)}
                      className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-[#00AAFF]/40 hover:bg-[#00AAFF]/5 text-left transition-colors"
                    >
                      <div className="w-10 h-10 rounded-lg bg-[#00AAFF]/10 flex items-center justify-center flex-shrink-0">
                        <PlayCircle className="w-5 h-5 text-[#00AAFF]" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">{video.title}</p>
                        {(video.tags || []).length > 0 && (
                          <p className="text-xs text-gray-500 truncate">
                            {(video.tags || []).map(tag => tag.name).join(', ')}
                          </p>
                        )}
                      </div>
                      <ArrowRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-950/70 animate-fade-in" onClick={() => setSelectedVideo(null)}>
          <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3 p-4 border-b border-gray-100">
              <div className="min-w-0">
                <h3 className="font-semibold text-gray-900 truncate">{selectedVideo.title}</h3>
                <p className="text-xs text-gray-500">Training video</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedVideo(null)}
                className="w-10 h-10 rounded-xl hover:bg-gray-100 flex items-center justify-center transition-colors"
                aria-label="Close video"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="bg-black aspect-video">
              <video
                src={videosApi.viewUrl(selectedVideo.id)}
                controls
                autoPlay
                className="w-full h-full"
              >
                Your browser cannot play this video.
              </video>
            </div>

            <div className="flex items-center justify-between gap-3 p-4">
              <p className="text-xs text-gray-500">Published training content managed by HR.</p>
              <a
                href={videosApi.viewUrl(selectedVideo.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#00AAFF] hover:bg-[#0088cc] text-white text-sm font-medium transition-colors"
              >
                Open video
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LoginPage;
